import { Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import type { AggregateResult } from "./aggregate";
import { formatValue } from "./format";
import { type ChartType } from "./spec";

/**
 * 每張圖的表格孿生版本。
 * 這不是「附加功能」—— light 模式的色盤有 3 個色位低於 3:1 對比，
 * 規則是必須提供可見標籤或表格檢視；而且 tooltip 永遠不能是讀到數字的唯一途徑。
 */
export default function TableView({
  result,
  chartType,
}: {
  result: AggregateResult;
  chartType: ChartType;
}) {
  const { t } = useTranslation();
  const {
    points,
    scatter,
    seriesNames,
    categories,
    valueLabel,
    valueFormat,
    additive,
    xField,
    yField,
  } = result;

  const { columns, data } = useMemo(() => {
    if (chartType === "scatter") {
      const cols: ColumnsType<Record<string, unknown>> = [
        {
          title: t("warRoom.table.item"),
          dataIndex: "label",
          key: "label",
          ellipsis: true,
        },
        ...(seriesNames.length > 1
          ? [
              {
                title:
                  result.seriesFieldMeta?.label ?? t("warRoom.table.series"),
                dataIndex: "series",
                key: "series",
              },
            ]
          : []),
        {
          title: xField?.label ?? "X",
          dataIndex: "x",
          key: "x",
          align: "right" as const,
          sorter: (a: Record<string, unknown>, b: Record<string, unknown>) =>
            Number(a.x) - Number(b.x),
          render: (v: number) => formatValue(v, xField?.format),
        },
        {
          title: yField?.label ?? "Y",
          dataIndex: "y",
          key: "y",
          align: "right" as const,
          sorter: (a: Record<string, unknown>, b: Record<string, unknown>) =>
            Number(a.y) - Number(b.y),
          render: (v: number) => formatValue(v, yField?.format),
        },
      ];
      return {
        columns: cols,
        data: scatter.map((p, i) => ({ key: i, ...p })),
      };
    }

    // 交叉表：列 = X 軸類別，欄 = 數列
    const byCategory = new Map<string, Record<string, number>>();
    for (const p of points) {
      let row = byCategory.get(p.x);
      if (!row) {
        row = {};
        byCategory.set(p.x, row);
      }
      row[p.series] = p.value;
    }
    const rows = categories.map((c, i) => {
      const row = byCategory.get(c) ?? {};
      const total = seriesNames.reduce((s, n) => s + (row[n] ?? 0), 0);
      return { key: i, category: c, ...row, __total: total };
    });

    const cols: ColumnsType<Record<string, unknown>> = [
      {
        title: xField?.label ?? t("warRoom.table.category"),
        dataIndex: "category",
        key: "category",
        fixed: "left",
        width: 200,
        ellipsis: true,
      },
      ...seriesNames.map((n) => ({
        title: n,
        dataIndex: n,
        key: n,
        align: "right" as const,
        sorter: (a: Record<string, unknown>, b: Record<string, unknown>) =>
          Number(a[n] ?? 0) - Number(b[n] ?? 0),
        render: (v: number | undefined) =>
          v === undefined ? "—" : formatValue(v, valueFormat),
      })),
      // 平均、極值、相異數加起來沒有意義 —— 寧可不給合計，也不給一個錯的數字
      ...(seriesNames.length > 1 && additive
        ? [
            {
              title: t("warRoom.table.total"),
              dataIndex: "__total",
              key: "__total",
              align: "right" as const,
              sorter: (
                a: Record<string, unknown>,
                b: Record<string, unknown>,
              ) => Number(a.__total) - Number(b.__total),
              render: (v: number) => formatValue(v, valueFormat),
            },
          ]
        : []),
    ];
    return { columns: cols, data: rows };
  }, [
    chartType,
    additive,
    points,
    scatter,
    seriesNames,
    categories,
    xField,
    yField,
    valueFormat,
    result.seriesFieldMeta,
    t,
  ]);

  return (
    <Table
      size="small"
      columns={columns}
      dataSource={data}
      pagination={{ pageSize: 20, size: "small", showSizeChanger: true }}
      scroll={{ x: "max-content" }}
      // 表格欄位要垂直對齊，這裡才用等寬數字
      style={{ fontVariantNumeric: "tabular-nums" }}
      summary={
        chartType === "scatter" || !additive
          ? undefined
          : () => (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0}>
                  <strong>{valueLabel}</strong>
                </Table.Summary.Cell>
                {seriesNames.map((n, i) => (
                  <Table.Summary.Cell key={n} index={i + 1} align="right">
                    <strong>
                      {formatValue(
                        points
                          .filter((p) => p.series === n)
                          .reduce((s, p) => s + p.value, 0),
                        valueFormat,
                      )}
                    </strong>
                  </Table.Summary.Cell>
                ))}
                {seriesNames.length > 1 ? (
                  <Table.Summary.Cell
                    index={seriesNames.length + 1}
                    align="right"
                  >
                    <strong>
                      {formatValue(
                        points.reduce((s, p) => s + p.value, 0),
                        valueFormat,
                      )}
                    </strong>
                  </Table.Summary.Cell>
                ) : null}
              </Table.Summary.Row>
            )
      }
    />
  );
}
